import API, { graphqlOperation } from "@aws-amplify/api";

import useAPI from "@toothfairy/shared-api/useApi";
import AppQuantumEditorUtils from "@toothfairy/shared-ui/AppQuantumEditorUtils";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import axios from "axios";
import appLogHelper from "../../API/AppLog/appLogHelper";
import { debounce } from "lodash-es";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { useReactiveVar } from "react-reactive-var";
import { displayedToasties, mrcSidebarCollapsed } from "../../../App";
import foldersHelper from "../../API/Folders/foldersHelper";
import Generator from "../../API/Generator";
import Searcher from "../../API/Searcher";
import trainingAPI from "../../API/Training/trainingAPI";
import Files from "../../API/Files";
import useAutoSave from "../../Hooks/useAutoSave";
import useFileSelector from "../../Hooks/useFileSelector";
import useRecording from "../../Hooks/useRecording";
import useTrainModeSwitcher from "../../Hooks/useTrainModeSwitcher";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../Router";
import { onUpdateDocumentEmbeddingStatus } from "../../graphql/subscriptions";
import { categorizeFiles } from "../../Utils/fileCategorization";
import { processDroppedItems } from "../../Utils/dropFolderTraversal";
import { isMobileScreen } from "../MainViewContainer";
import MrcDocumentsView from "./MrcDocumentsView";
import { makeGraphQLRequest, subscribeGraphQL } from "../../API";
import envConfig from "../../../envConfig";

const MrcDocumentsContainer = () => {
  const { documentId } = useParams();
  const [searchText, setSearchText] = useState();
  const [publishOnUpload, setPublishOnUpload] = useState(true);
  const [uploadAdvEmb, setUploadAdvEmb] = useState(false);
  const [uploadExtractImages, setUploadExtractImages] = useState(false);
  const [uploadFolderName, setUploadFolderName] = useState("");
  const [uploadFileCount, setUploadFileCount] = useState(0);
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState(null);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [bulkMoveTargetFolderId, setBulkMoveTargetFolderId] = useState(null);
  const [bulkMoveTopics, setBulkMoveTopics] = useState(null);
  const [bulkMovePublish, setBulkMovePublish] = useState(true);
  const [isBulkMoveInProgress, setIsBulkMoveInProgress] = useState(false);
  const [categorizedFiles, setCategorizedFiles] = useState({
    pdfFiles: [],
    genericFiles: [],
  });
  const [dropFolderFiles, setDropFolderFiles] = useState([]);
  const [dropFolderName, setDropFolderName] = useState("");
  const dropTargetNodeRef = useRef(null);
  const inDropUploadRef = useRef(false);
  const dropCreatedRef = useRef([]);
  const [dropTargetNode, setDropTargetNode] = useState(null);
  const history = useHistory();
  const { goToTrainMode } = useTrainModeSwitcher({
    route: "mrc_contents",
    isMrc: true,
  });
  const { transcript, startRecording, stopRecording } = useRecording({
    onForcedStop: () => {},
    maxTimeForRecording: 3600,
  });
  const [embeddingStatus, setEmbeddingStatus] = useState("not_ready");
  const [isExtractImagesAllowed, setIsExtractImagesAllowed] = useState(false);
  const [embeddingProgress, setEmbeddingProgress] = useState(0);
  const [selectedNodeForTreeview, setSelectedNodeForTreeView] = useState(null);
  function findParentById(tree, id, parent = null) {
    for (const node of tree) {
      if (node.id === id) {
        return parent;
      }

      if (node.children && node.children.length > 0) {
        const result = findParentById(node.children, id, node);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }
  // create a similar function that finds mrcRoot with the id of the node and returns the entire node
  function findNodeById(tree, id) {
    for (const node of tree) {
      if (node.id === id) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const result = findNodeById(node.children, id);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  const {
    loading: vectorSearchInProgress,
    apiRequest: vectorSearchRequest,
    wasInvoked: vectorSearchInvoked,
    data: vectorSearchResult,
  } = useAPI(Searcher.getDocumentsByVectorDB, null, () => {});
  const {
    loading: embeddingsByDocumentInProgress,
    apiRequest: embeddingsByDocumentRequest,
    wasInvoked: embeddingsByDocumentInvoked,
    data: embeddingsByDocumentResult,
  } = useAPI(trainingAPI.getEmbeddingByDocumentID, null, () => {});
  const {
    getActiveWorkspace,
    getWorkspaceEntities,
    agcEnabled,
  } = useWorkspaces();
  const fetchEmbeddingsByDocument = () => {
    embeddingsByDocumentRequest(documentId, getActiveWorkspace()?.id);
  };

  // Debounced function to handle API call
  const _loadSuggestions = (query, workspaceid, token) => {
    return vectorSearchRequest({
      text: query,
      workspaceid: workspaceid,
      topK: 5,
      token: token,
    }).then((data) => {
      if (data && data?.length > 0) {
        // Map the results to the expected format for react-select
        return data.map((chunk) => ({
          value: chunk.chunk_id,
          label: `${Math.floor(chunk.cosinesim * 100)}% - ${chunk.title}`,
          title: chunk.title,
        }));
      } else {
        return [];
      }
    });
  };
  const loadSuggestions = React.useCallback(
    debounce((inputText, callback) => {
      _loadSuggestions(
        inputText,
        getActiveWorkspace()?.id,
        getActiveWorkspace()?.workspaceToken,
      ).then((options) => callback(options));
    }, 300),
    [getActiveWorkspace()?.id, getActiveWorkspace()?.workspaceToken],
  );

  // const loadSuggestions = throttle(_loadSuggestions, 300);

  const [showModal, setShowModal] = React.useState(false);
  const [deletionInProgress, setDeletionInProgress] = React.useState(false);
  const isCollapsed = useReactiveVar(mrcSidebarCollapsed);
  const setIsCollapsed = (value) => mrcSidebarCollapsed(value);
  const [treeViewActionContext, setTreeViewActionContext] = useState("");
  const [topics, setTopics] = useState([]);
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const { user } = AppUser.useUser();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const documents = state?.mrcDocuments;
  const folders = state?.mrcFolders;
  const setDocuments = (documents) => {
    dispatch("SET_MRC_DOCUMENTS", documents);
  };
  const setFolders = (folders) => {
    dispatch("SET_MRC_FOLDERS", folders);
  };
  useEffect(() => {
    setIsPDFLoaded(false);
  }, []);
  useEffect(() => {
    setTopics(
      getWorkspaceEntities("topic")?.map((topic) => {
        topic.name = topic?.label;
        topic.category = topic.type;
        return topic;
      }),
    );
  }, [JSON.stringify(getWorkspaceEntities("topic"))]);
  const onTopicCreated = () => {
    setTopics(
      getWorkspaceEntities("topic")?.map((topic) => {
        topic.name = topic?.label;
        topic.category = topic.type;
        return topic;
      }),
    );
  };
  const {
    loading: documentFetchInProgress,
    apiRequest: fetchMRCData,
    wasInvoked: fetchMRCDataInvoked,
  } = useAPI(trainingAPI.getDocumentsByWorkspace, null, () => {});
  const {
    loading: documentTreeStructureSavingInProgress,
    apiRequest: updateDocumentTreeStructure,
  } = useAPI(trainingAPI.updateTrainingData, null, () => {});
  const {
    loading: documentDeletionInProgress,
    apiRequest: deleteDocument,
  } = useAPI(trainingAPI.deleteTrainingData, null, () => {});

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeToDelete, setSelectedNodeToDelete] = useState(null);
  const [isMultiDeleteMode, setIsMultiDeleteMode] = useState(false);
  const [multiSelectedIds, setMultiSelectedIds] = useState([]);
  const getSelectedFolder = () => {
    if (selectedNode?.data?.droppable) return selectedNode?.id;
    else if (selectedNode && !selectedNode?.data?.droppable)
      return selectedNode?.parent;
    else return "root";
  };
  const onPDFDocumentLoad = (pages) => {
    setIsPDFLoaded(true);
    setIsExtractImagesAllowed(pages < 51);
  };
  const [selectedTopicsForPdfUpload, setSelectedTopicsForPdfUpload] = useState(
    [],
  );
  const onSelectedTopicsChangeForPdfUpload = (v) => {
    setSelectedTopicsForPdfUpload(v);
  };
  const {
    value,
    editor,
    isInactive,
    updateAPIError,
    updateAPIInProgress,
    createAPIInProgress,
    getAPIRequest,
    saveDocument,
    handleSocketConnectionChange,
    handleNewMRCDocument,
    saveDocumentTitle,
    setValue,
    setDocumentStatus,
    documentStatus,
    getAPIResult,
    selectedTopics,
    setSelectedTopics,
    isSaved,
    setIsLoading,
    setEditor,
    isLoading,
    isReadyForRTC,
    isDocumentValid,
    generateAdaptedTopics,
    creationofNewDocumentInProgress,
    wordsCount,
    charactersCount,
    fakeCheckAPIResult,
    fakeCheckAPIInProgress,
    isTopicUpdateModeEnabled,
    setIsTopicUpdateModeEnabled,
    setIsPDFLoaded,
    isPDFLoaded,
    isPDF,
    extractImages,
    setExtractImages,
    advEmb,
    setAdvEmb,
    documentImgRetrievalInstructions,
    setDocumentImgRetrievalInstructions,
    setLastCancellation,
    lastrequestTime,
    setIsSaveRequested,
  } = useAutoSave({
    documentId,
    workspaceid: getActiveWorkspace()?.id,
    user,
    updateMethod: trainingAPI.updateTrainingData,
    createMethod: trainingAPI.postTrainingData,
    route: "mrc_contents",
    documentType: "readComprehensionContent",
    backupProperty: "initialMRCValue",
    backupCommand: "SET_INITIAL_MRC_VALUE",
    savesSkipsForBooting: 0,
    isAGCEnabled: agcEnabled,
    fakeCheckMethod: Generator.fakeCheck,
    fakeCheckScope: "fakeContentCheck",
    fakeCheckExecutionInterval: 1000,
    fakeCheckSkipsForBooting: 0,
  });
  const handleImagesExtraction = (v) => {
    setDocumentImgRetrievalInstructions(
      v?.documentImgRetrievalInstructions || "",
    );
    setIsSaveRequested(Date.now()?.toString());
  };
  const handleOnRecorderClick = (v) => {
    try {
      if (v) {
        startRecording();
        // show the toast message
        AppToasty.show(t("chats.recordingStarted"), {
          placement: "bottom",
          type: "success",
        });
      } else {
        stopRecording();
        AppToasty.show(t("chats.recordingStopped"), {
          placement: "bottom",
          type: "success",
        });
      }
    } catch (error) {
      AppToasty.show(t("chats.recordingError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  useEffect(() => {
    if (transcript) {
      AppQuantumEditorUtils.Utilities.insertTextAtSelection(
        editor,
        transcript || "",
      );

      setEditor(editor);
      setValue(editor?.children);
    }
  }, [transcript]);
  useEffect(() => {
    if (isMobileScreen()) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [isMobileScreen()]);

  useEffect(() => {
    if (getAPIResult?.data?.length && getActiveWorkspace()?.id && !isLoading) {
      const sTopics = getAPIResult?.data[0]?.topics?.map((u) => {
        return {
          name: topics.filter((z) => z.id === u)[0]?.label,
          category: topics.filter((z) => z.id === u)[0]?.type,
          id: u,
        };
      });
      setSelectedTopics(sTopics || []);
      setDocumentStatus(getAPIResult?.data[0]?.status);
      setDocumentImgRetrievalInstructions(
        getAPIResult?.data[0]?.img_retrieval_prompt,
      );
      setEmbeddingStatus(getAPIResult?.data[0]?.embedding_status);
      setEmbeddingProgress(getAPIResult?.data[0]?.completion_percentage || 0);
      setIsTopicUpdateModeEnabled(false);
      // saveDocument({ documentId, value, topics: sTopics?.map((u) => u.id) });
    }
  }, [
    lastrequestTime,
    JSON.stringify(getAPIResult?.data),
    JSON.stringify(topics),
    documentId,
    isLoading,
    selectedNode?.id,
  ]);
  useEffect(() => {
    let subscriptionObj;
    const setupSubscription = async () => {
      if (documentId) {
        subscriptionObj = await subscribeGraphQL(
          graphqlOperation(onUpdateDocumentEmbeddingStatus, {
            id: documentId.toString(),
          }),
          {
            next: async (data) => {
              if (
                data?.value?.data?.onUpdateDocumentEmbeddingStatus?.id?.toString() !==
                documentId.toString()
              )
                return;
              const embeddingStatus =
                data?.value?.data?.onUpdateDocumentEmbeddingStatus
                  ?.embedding_status || "not_ready";
              const embeddingProgress =
                data?.value?.data?.onUpdateDocumentEmbeddingStatus
                  ?.completion_percentage || 0;

              setEmbeddingProgress(
                embeddingStatus === "ready" ? 100 : embeddingProgress,
              );
              setEmbeddingStatus(embeddingStatus);
            },
            error: (error) => {
              console.log("Error on sub emb status", error);
            },
          },
        );
      }
    };
    setupSubscription();
    return () => {
      if (subscriptionObj) {
        console.log("SUBSCERIP", subscriptionObj);
        subscriptionObj?.unsubscribe();
      }
    };
  }, [documentId]);
  useEffect(() => {
    if (documentId && getActiveWorkspace()?.id) {
      setSelectedNode({
        id: documentId,
        data: {
          ...selectedNode?.data,
          external_path: documents?.find((u) => u.id == documentId)
            ?.external_path,
        },
      });
    }
  }, [documentId, getActiveWorkspace()?.id, documents?.length]);
  // check here
  const handleDocumentSelection = (v) => {
    const resultObject = vectorSearchResult?.find(
      (u) => u.chunk_id === v?.value,
    );

    const _selectedDocument = state.treeViewData?.find(
      (u) => u.id === resultObject?.doc_id,
    );
    if (!_selectedDocument && v?.title?.startsWith("http")) {
      window.open(v?.title, "_blank");
    } else if (_selectedDocument?.id) {
      handleClickOnNode({
        id: _selectedDocument?.id,
        data: _selectedDocument,
      });
    }
  };
  const removeNode = async (node) => {
    setShowModal(false);
    if (state.treeViewData?.find((u) => u.id == node?.id)?.children?.length > 0)
      return AppToasty.show(t("errors.folderNotEmpty"), {
        placement: "bottom",
        type: "danger",
      });
    setDeletionInProgress(true);
    setIsLoading(true);
    if (selectedNode?.id === selectedNodeToDelete.id) {
      history.push(`/workspaces/${getActiveWorkspace()?.id}/mrc_contents`);
      handleClickOnNode(null);
    }
    if (node?.data?.droppable) {
      setFolders(folders.filter((u) => u.id?.toString() != node?.id));
      const result = await foldersHelper.deleteFolder(node?.id);
    } else {
      setDocuments(documents.filter((u) => u.id?.toString() != node?.id));
      const result = await deleteDocument(node?.id, getActiveWorkspace()?.id);
    }
    setDeletionInProgress(false);
    setIsLoading(false);
  };
  const updateNodeName = (node, name) => {
    let itemIndex;
    if (node?.data?.droppable) {
      itemIndex = state?.mrcFolders.findIndex((u) => u.id == node.id);
      let newFolder = { ...state.mrcFolders[itemIndex] };
      newFolder.name = name;
      state.mrcFolders[itemIndex] = newFolder;
      dispatch("SET_MRC_FOLDERS", [...state.mrcFolders]);
    } else {
      itemIndex = state?.mrcDocuments.findIndex((u) => u.id == node.id);
      state.mrcDocuments[itemIndex].title = name;
      dispatch("SET_MRC_DOCUMENTS", [...state.mrcDocuments]);
    }
  };

  const createFolderForUpload = async (folderName, parentId = null) => {
    try {
      const parent =
        parentId ||
        resolvedUploadTargetId ||
        (selectedNodeForTreeview?.data?.droppable
          ? selectedNodeForTreeview?.id
          : selectedNodeForTreeview?.data?.parent) ||
        "root";

      const newFolder = {
        parent: parent,
        droppable: true,
        name: folderName,
        workspaceID: getActiveWorkspace()?.id,
      };

      const result = await foldersHelper.createFolder(newFolder);
      const newFolderId = result?.data?.createFolders?.id;

      dispatch("SET_MRC_TREEVIEW_DATA", [
        { ...newFolder, id: newFolderId },
        ...state?.treeViewData,
      ]);
      setFolders([...folders, { ...newFolder, id: newFolderId }]);

      return newFolderId;
    } catch (error) {
      console.error(error);
      AppToasty.show(t("toasts.errorCreatingFolder"), {
        type: "danger",
      });
      return null;
    }
  };

  const SaveFolder = useCallback(
    async (node, folderName, isExisting = false) => {
      if (isExisting) {
        try {
          await foldersHelper.updateFolder(selectedNodeForTreeview?.id, [
            { property: "name", value: folderName },
          ]);
          updateNodeName(selectedNodeForTreeview, folderName);
        } catch (error) {
          console.error(error);
          AppToasty.show(t("toasts.errorUpdatingFolder"), {
            type: "danger",
          });
        }
      } else {
        try {
          // this method break if the selectedFolder is the one selected in teh treeview based on the route
          const newFolder = {
            parent: selectedNodeForTreeview?.data?.droppable
              ? selectedNodeForTreeview?.id
              : selectedNodeForTreeview?.data?.parent || "root",
            droppable: true,
            name: folderName,
            workspaceID: getActiveWorkspace()?.id,
          };

          const result = await foldersHelper.createFolder(newFolder);
          dispatch("SET_MRC_TREEVIEW_DATA", [
            { ...newFolder, id: result?.data?.createFolders?.id },
            ...state?.treeViewData,
          ]);
          setFolders([
            ...folders,
            { ...newFolder, id: result?.data?.createFolders?.id },
          ]);
        } catch (error) {
          console.error(error);
          AppToasty.show(t("toasts.errorCreatingFolder"), {
            type: "danger",
          });
        }
      }
      setShowModal(false);
    },
    [state?.treeViewData, selectedNodeForTreeview?.id],
  );
  const handleDocumentStatusChange = (status) => {
    setDocumentStatus(status);
  };
  const handleClickOnNode = async (node, toDelete = false) => {
    if (toDelete) setSelectedNodeToDelete(node);
    else {
      setSelectedNodeForTreeView(node);
      if (node?.data?.droppable === true) return;
      setSelectedNode(node);
      if (node?.data?.type) {
        setIsPDFLoaded(node?.id === selectedNode?.id);

        history.push(
          `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${node?.id}`,
        );
      } else if (
        node &&
        !node?.data?.droppable &&
        node?.id !== selectedNode?.id &&
        node?.id !== documentId
      ) {
        setIsLoading(true);
        setSelectedTopics([]);
        goToTrainMode({
          docID: node.id,
        });
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      }
    }
  };
  const setDialogActionOnTree = useCallback(
    (action) => {
      setShowModal(true);
      setTreeViewActionContext(action);
    },
    [setShowModal, setTreeViewActionContext],
  );
  const deleteNode = async (node) => {
    try {
      setDeletionInProgress(true);
      setShowModal(false);
      setIsLoading(true);
      if (selectedNode?.id === selectedNodeToDelete.id) {
        // const indexOfDeletedNode =
        //   state?.treeViewData
        //     ?.filter((u) => !u.droppable)
        //     .map((u) => u.id)
        //     ?.indexOf(selectedNodeToDelete.id) - 1;
        handleClickOnNode(null);
        history.push(`/workspaces/${getActiveWorkspace()?.id}/mrc_contents`);
      }

      const treeToDelete = treeify(
        state?.treeViewData,
        "id",
        "parent",
        "children",
        node?.id,
      );
      const flattenedList = treeToDelete.flat();
      await Promise.all(
        flattenedList.map((u) => {
          if (u?.data?.droppable) return foldersHelper.deleteFolder(u.id);
          else
            return trainingAPI.deleteTrainingData(
              u.id,
              getActiveWorkspace()?.id,
            );
        }),
      );
      dispatch("SET_MRC_TREEVIEW_DATA", [
        ...state?.treeViewData?.filter((u) => u?.id !== node?.id),
      ]);

      setTimeout(() => {
        setDeletionInProgress(false);
        setIsLoading(false);
      }, 2000);
    } catch (error) {
      console.error(error);
      AppToasty.show(t("toasts.errorDeletingFolder"), {
        type: "danger",
      });
    }
  };
  const toggleMultiDeleteMode = () => {
    setIsMultiDeleteMode((prev) => {
      if (prev) setMultiSelectedIds([]);
      return !prev;
    });
  };

  const toggleNodeSelection = (nodeId) => {
    setMultiSelectedIds((prev) =>
      prev.includes(nodeId)
        ? prev.filter((id) => id !== nodeId)
        : [...prev, nodeId],
    );
  };

  const collectSubtree = (flatList, nodeId) => {
    const result = [];
    const queue = [nodeId];
    const visited = new Set();
    while (queue.length > 0) {
      const currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const node = flatList.find((n) => n.id == currentId);
      if (node) result.push(node);
      const children = flatList.filter((n) => n.parent == currentId);
      children.forEach((c) => queue.push(c.id));
    }
    return result;
  };

  const deleteMultipleNodes = async (selectedIds) => {
    try {
      setDeletionInProgress(true);
      setShowModal(false);
      setIsLoading(true);

      const fixedIds = [
        "mrcRoot",
        "qaRoot",
        "domainRoot",
        "artifactsRoot",
        "canvasRoot",
        "tfcodeSession",
      ];
      const flatList = state?.treeViewData || [];

      const idsToDeleteSet = new Set(
        selectedIds.filter((id) => !fixedIds.includes(id)),
      );

      const allFoldersToDelete = [];
      const allFilesToDelete = [];
      const allDeletedIds = new Set();

      idsToDeleteSet.forEach((id) => {
        const subtree = collectSubtree(flatList, id);
        const subtreeFolders = subtree.filter(
          (n) => n.droppable && !fixedIds.includes(n.id),
        );
        const subtreeFiles = subtree.filter((n) => !n.droppable);

        subtreeFiles.forEach((f) => {
          allFilesToDelete.push(f);
          allDeletedIds.add(f.id);
        });
        subtreeFolders.forEach((f) => {
          allFoldersToDelete.push(f);
          allDeletedIds.add(f.id);
        });
      });

      const uniqueFiles = allFilesToDelete.filter(
        (f, i, arr) => arr.findIndex((x) => x.id == f.id) === i,
      );
      const uniqueFolders = allFoldersToDelete.filter(
        (f, i, arr) => arr.findIndex((x) => x.id == f.id) === i,
      );

      const getDepth = (nodeId, list, depth = 0) => {
        const node = list.find((n) => n.id == nodeId);
        if (!node || !node.parent || node.parent === "root") return depth;
        return getDepth(node.parent, list, depth + 1);
      };

      uniqueFolders.sort(
        (a, b) => getDepth(b.id, flatList) - getDepth(a.id, flatList),
      );

      await Promise.all(
        uniqueFiles.map((f) =>
          trainingAPI.deleteTrainingData(f.id, getActiveWorkspace()?.id),
        ),
      );

      for (const folder of uniqueFolders) {
        await foldersHelper.deleteFolder(folder.id);
      }

      if (allDeletedIds.has(selectedNode?.id)) {
        handleClickOnNode(null);
        history.push(`/workspaces/${getActiveWorkspace()?.id}/mrc_contents`);
      }

      setDocuments(
        documents.filter((d) => !allDeletedIds.has(d.id?.toString())),
      );
      setFolders(folders.filter((f) => !allDeletedIds.has(f.id?.toString())));
      dispatch("SET_MRC_TREEVIEW_DATA", [
        ...flatList.filter((u) => !allDeletedIds.has(u.id)),
      ]);

      setIsMultiDeleteMode(false);
      setMultiSelectedIds([]);

      setTimeout(() => {
        setDeletionInProgress(false);
        setIsLoading(false);
      }, 2000);
    } catch (error) {
      console.error(error);
      AppToasty.show(t("toasts.errorDeletingFolder"), {
        type: "danger",
      });
      setDeletionInProgress(false);
      setIsLoading(false);
    }
  };

  const bulkMoveNodes = async ({
    selectedIds,
    targetFolderId,
    topics,
    publish,
  }) => {
    try {
      setIsBulkMoveInProgress(true);
      const fixedIds = [
        "mrcRoot",
        "qaRoot",
        "domainRoot",
        "artifactsRoot",
        "canvasRoot",
        "tfcodeSession",
      ];
      const flatList = state?.treeViewData || [];
      const ids = (selectedIds || []).filter((id) => !fixedIds.includes(id));

      // "No folder change" => keep current folder, only apply topics/status
      const effectiveTargetFolderId =
        targetFolderId && targetFolderId !== "__no_folder_change__"
          ? targetFolderId
          : null;

      const newTopicIds =
        topics && topics.length > 0
          ? topics.map((u) => (typeof u === "object" ? u?.id : u))
          : null;
      const newStatus =
        publish == null ? null : publish ? "published" : "draft";

      for (const id of ids) {
        const node = flatList.find((n) => n.id === id);
        if (!node) continue;
        if (node.droppable) {
          if (effectiveTargetFolderId) {
            const index = folders
              .map((u) => u.id?.toString())
              .indexOf(node.id?.toString());
            if (index >= 0) {
              folders[index] = {
                ...folders[index],
                parent: effectiveTargetFolderId,
              };
            }
            await foldersHelper.updateFolder(node.id, [
              { property: "parent", value: effectiveTargetFolderId },
            ]);
          }
        } else {
          const fields = {
            id: node.id,
            workspaceid: getActiveWorkspace()?.id,
          };
          if (effectiveTargetFolderId)
            fields.folderid = effectiveTargetFolderId;
          if (newTopicIds) fields.topics = newTopicIds;
          if (newStatus) fields.status = newStatus;
          const index = documents
            .map((u) => u.id?.toString())
            .indexOf(node.id?.toString());
          if (index >= 0 && effectiveTargetFolderId) {
            documents[index] = {
              ...documents[index],
              folderid: effectiveTargetFolderId,
            };
          }
          await updateDocumentTreeStructure({ fields });
        }
      }

      // Apply topics/status to documents nested inside selected folders too
      if (newTopicIds || newStatus) {
        const nestedDocIds = new Set();
        ids.forEach((id) => {
          const node = flatList.find((n) => n.id === id);
          if (node?.droppable) {
            collectSubtree(flatList, id)
              .filter((n) => !n.droppable)
              .forEach((n) => nestedDocIds.add(n.id));
          }
        });
        for (const docId of nestedDocIds) {
          const fields = { id: docId, workspaceid: getActiveWorkspace()?.id };
          if (newTopicIds) fields.topics = newTopicIds;
          if (newStatus) fields.status = newStatus;
          await updateDocumentTreeStructure({ fields });
        }
      }

      setFolders([...folders]);
      setDocuments([...documents]);
      if (effectiveTargetFolderId) {
        dispatch("SET_MRC_TREEVIEW_DATA", [
          ...flatList.map((n) =>
            ids.includes(n.id) ? { ...n, parent: effectiveTargetFolderId } : n,
          ),
        ]);
      }

      setShowBulkMoveModal(false);
      setIsMultiDeleteMode(false);
      setMultiSelectedIds([]);
      AppToasty.show(
        t("mrcdocs.toasts.bulkUpdateSuccess", {
          defaultValue: "Items updated",
        }),
        { type: "success" },
      );
    } catch (error) {
      console.error("bulkMoveNodes error", error);
      AppToasty.show(
        t("mrcdocs.toasts.bulkUpdateFailed", {
          defaultValue: "Failed to update items",
        }),
        { type: "danger" },
      );
    } finally {
      setIsBulkMoveInProgress(false);
    }
  };

  const createMRCDocument = async (title, topics, status) => {
    setIsLoading(true);
    let locationNode = selectedNodeForTreeview;
    if (!selectedNodeForTreeview?.data?.droppable) {
      const parent = findParentById(
        state.treeViewData,
        selectedNodeForTreeview?.id,
      );
      locationNode = {
        id: parent?.id,
        data: parent,
      };
    }
    if (!locationNode?.id) {
      const parent = findNodeById(state.treeViewData, "mrcRoot");
      locationNode = {
        id: parent?.id,
        data: parent,
      };
    }
    await handleNewMRCDocument(true, locationNode, title, topics, status);
    setShowModal(false);
    setIsLoading(false);
    setSelectedTopics(topics);
  };
  const editDocumentTitle = async (title) => {
    try {
      await saveDocumentTitle({
        documentId,
        workspaceid: getActiveWorkspace()?.id,
        title,
      });
      updateNodeName(selectedNodeForTreeview, title);
      setShowModal(false);
    } catch (error) {
      console.error(error);
      AppToasty.show(t("toasts.errorUpdatingDocument"), {
        type: "danger",
      });
    }
  };

  const { apiRequest: S3downloadUrlRequest } = useAPI(
    Files.downloadUrlGeneration,
    envConfig,
  );

  const { apiRequest: S3uploadUrlRequest } = useAPI(
    trainingAPI.bulkImportUrlGeneration,
    envConfig,
  );

  const [
    isUpdateFileUploadInProgress,
    setIsUpdateFileUploadInProgress,
  ] = useState(false);
  const updateFileInputRef = useRef(null);

  const downloadDocument = async (externalPath) => {
    try {
      if (!externalPath) {
        AppToasty.show(t("mrcdocs.toasts.noDocumentToDownload"), {
          type: "warning",
        });
        return;
      }

      const _fileExt = externalPath?.split(".")?.pop()?.toLowerCase();
      const _artifactExtensions = [
        "png",
        "jpg",
        "jpeg",
        "gif",
        "webp",
        "svg",
        "avif",
        "mp3",
        "m4a",
        "aac",
        "flac",
        "ogg",
        "mp4",
        "webm",
        "mov",
      ];
      const _isArtifact = _artifactExtensions.includes(_fileExt);

      const result = await S3downloadUrlRequest({
        workspaceid: getActiveWorkspace()?.id,
        logId: null,
        type: _fileExt,
        filename: externalPath.replace(
          `s3://files-to-import-${envConfig.REACT_APP_REGION}-${envConfig.REACT_APP_ENV}/`,
          "",
        ),
        context: _isArtifact ? "designArtifact" : "pdf",
      });

      if (result?.url) {
        const response = await fetch(result.url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = externalPath.split("/").pop() || "document.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        AppToasty.show(t("mrcdocs.toasts.downloadStarted"), {
          type: "success",
        });
      }
    } catch (error) {
      console.error(error);
      AppToasty.show(t("mrcdocs.toasts.downloadFailed"), { type: "danger" });
    }
  };

  const getContentTypeByExt = (ext) => {
    ext = (ext || "").toLowerCase();
    if (ext === "pdf") return "application/pdf";
    if (ext === "txt") return "text/plain";
    if (ext === "md") return "text/markdown";
    if (ext === "html") return "text/html";
    if (ext === "csv") return "text/csv";
    if (["png", "jpg", "jpeg", "gif", "webp", "avif"].includes(ext))
      return `image/${ext === "jpg" ? "jpeg" : ext}`;
    if (ext === "svg") return "image/svg+xml";
    if (["mp3", "m4a", "aac", "flac", "ogg"].includes(ext))
      return `audio/${ext}`;
    if (["mp4", "webm", "mov"].includes(ext)) return `video/${ext}`;
    return "application/octet-stream";
  };

  const onUpdateFileClick = () => {
    if (!selectedNode?.data?.external_path || isUpdateFileUploadInProgress)
      return;
    updateFileInputRef?.current?.click();
  };

  const updateDocumentFile = async (file) => {
    if (!file) return;
    const oldExternalPath = selectedNode?.data?.external_path;
    if (!documentId || !oldExternalPath) {
      AppToasty.show(t("mrcdocs.toasts.fileUpdateFailed"), {
        type: "danger",
      });
      return;
    }
    const originalExt = oldExternalPath?.split(".").pop().toLowerCase();
    const newExt = file?.name?.split(".").pop().toLowerCase();
    if (originalExt && newExt && originalExt !== newExt) {
      AppToasty.show(t("mrcdocs.toasts.fileTypeMismatch"), {
        type: "danger",
      });
      return;
    }
    try {
      setIsUpdateFileUploadInProgress(true);
      AppToasty.show(t("mrcdocs.toasts.fileUpdateStarted"), {
        type: "success",
      });

      const s3Prefix = `s3://files-to-import-${envConfig.REACT_APP_REGION}-${envConfig.REACT_APP_ENV}/`;
      const importType =
        oldExternalPath.replace(s3Prefix, "").split("/")[0] || "imported-files";

      const _appLog = await appLogHelper.createAppLog({
        status: "dispatched",
        workspaceID: getActiveWorkspace()?.id,
        type: "documentImport",
        event: "readComprehension file update",
        filename: file.name,
        createdBy: user?.id,
      });

      const filename = `${getActiveWorkspace()?.id}/${_appLog?.id}.${newExt}`;

      const contentType = getContentTypeByExt(newExt);

      const result = await S3uploadUrlRequest({
        filename,
        importType,
        contentType,
      });

      await axios.request({
        method: "put",
        url: JSON.parse(result?.body)?.uploadURL,
        data: file,
        headers: {
          "Content-Type": contentType,
        },
      });

      const newExternalPath = `${s3Prefix}${importType}/${filename}`;

      await updateDocumentTreeStructure({
        fields: {
          id: documentId,
          workspaceid: getActiveWorkspace()?.id,
          external_path: newExternalPath,
          userID: user?.id,
        },
      });

      const docIndex = documents.findIndex(
        (u) => u?.id?.toString() === documentId?.toString(),
      );
      if (docIndex >= 0) {
        const updatedDocs = [...documents];
        updatedDocs[docIndex] = {
          ...updatedDocs[docIndex],
          external_path: newExternalPath,
          type: newExt,
        };
        setDocuments(updatedDocs);
      }

      if (state?.treeViewData) {
        const updatedTree = state.treeViewData.map((u) =>
          u?.id?.toString() === documentId?.toString()
            ? {
                ...u,
                data: {
                  ...(u?.data || {}),
                  external_path: newExternalPath,
                  type: newExt,
                },
              }
            : u,
        );
        dispatch("SET_MRC_TREEVIEW_DATA", updatedTree);
      }

      setSelectedNode((prev) => ({
        id: documentId,
        data: {
          ...(prev?.data || {}),
          external_path: newExternalPath,
          type: newExt,
        },
      }));

      setIsPDFLoaded(false);

      AppToasty.show(t("mrcdocs.toasts.fileUpdateSuccess"), {
        type: "success",
      });
    } catch (error) {
      console.error("updateDocumentFile error", error);
      AppToasty.show(t("mrcdocs.toasts.fileUpdateFailed"), {
        type: "danger",
      });
    } finally {
      setIsUpdateFileUploadInProgress(false);
      if (updateFileInputRef?.current) updateFileInputRef.current.value = "";
    }
  };

  const onSelectedTopicsChange = useCallback(
    (selectedItems) => {
      setSelectedTopics(selectedItems);
    },
    [setSelectedTopics, isTopicUpdateModeEnabled],
  );
  useEffect(() => {
    if (getActiveWorkspace()?.id) loadTrainingData();
  }, [getActiveWorkspace()?.id]);
  const loadTrainingData = async () => {
    await getDocuments();
  };
  const getDocuments = async () => {
    const workspaceID = getActiveWorkspace()?.id;
    const folders = await foldersHelper.getFoldersByWorkspaceID(workspaceID);
    const promise1 = fetchMRCData({
      workspaceID,
      type: "readComprehensionContent",
      fields: ["id", "folderid", "title"],
      page: -1,
      pageLimit: 5000,
    });

    const promise2 = fetchMRCData({
      workspaceID,
      type: "questionAnswering",
      fields: ["id", "folderid", "title"],
      page: -1,
      pageLimit: 5000,
    });

    const promise3 = fetchMRCData({
      workspaceID,
      type: "readComprehensionPdf",
      fields: ["id", "folderid", "title", "external_path"],
      page: -1,
      pageLimit: 5000,
    });
    const promise4 = fetchMRCData({
      workspaceID,
      type: "readComprehensionFile",
      fields: ["id", "folderid", "title", "external_path"],
      page: -1,
      pageLimit: 5000,
    });
    // Enable the retrieval of the html and website pages
    const promise5 = fetchMRCData({
      workspaceID,
      type: "readComprehensionUrl",
      fields: ["id", "folderid", "title", "external_path", "type"],
      page: -1,
      pageLimit: 5000,
    });
    const promise6 = fetchMRCData({
      workspaceID,
      type: "designArtifact",
      fields: ["id", "folderid", "title", "external_path", "type"],
      page: -1,
      pageLimit: 5000,
    });

    const [
      result,
      qaResult,
      pdfResult,
      fileResult,
      urlResult,
      artifactResult,
    ] = await Promise.all([
      promise1,
      promise2,
      promise3,
      promise4,
      promise5,
      promise6,
    ]);

    const documents = [
      ...result?.data,
      ...qaResult?.data,
      ...pdfResult?.data,
      ...fileResult?.data,
      ...urlResult?.data,
      ...artifactResult?.data,
    ]?.map((u) => {
      return {
        external_path: u?.external_path,
        folderid: u?.folderid,
        id: u?.id,
        title: `${u?.title}${
          u?.external_path ? "." + u?.external_path?.split(".")?.pop() : ""
        }`,
        type: u?.type,
      };
    });

    setDocuments(documents);
    setFolders(
      folders?.data?.foldersByWorkspaceID?.items?.filter(
        (u) => u?._deleted != true,
      ),
    );
    dispatch("SET_FOLDERS_LOADED", true);
  };
  useEffect(() => {
    if (state?.foldersLoaded) adaptFlatListToTreeView(documents, folders);
  }, [documents, folders, state?.foldersLoaded]);
  useEffect(() => {
    if (state?.treeViewData) {
      const found = state.treeViewData.find((u) => u?.id == "3537");
    }
  }, [state?.treeViewData]);
  const adaptFlatListToTreeView = (documents, folders) => {
    const adaptedFiles =
      documents?.map((u) => {
        return {
          id: u.id.toString(),
          parent: u.folderid
            ? u.folderid
            : u?.type == "readComprehensionUrl"
            ? "domainRoot"
            : u?.type == "designArtifact"
            ? "artifactsRoot"
            : "mrcRoot",
          name: u.title || t("mrcdocs.ui.noTitle"),
          droppable: false,
          type: u?.external_path && u?.external_path?.split(".")?.pop(),
          external_path: u?.external_path,
        };
      }) || [];

    const treeData = [
      ...(folders?.map((u) => ({ ...u, droppable: true, children: [] })) || []),
      {
        id: "mrcRoot",
        droppable: true,
        name: "📕 General content",
        status: "active",
        parent: "root",
        readOnly: true,
      },
      {
        id: "qaRoot",
        droppable: true,
        name: "📘 Q&A",
        status: "active",
        parent: "root",
        readOnly: true,
      },
      {
        id: "domainRoot",
        droppable: true,
        name: "🌐 Domains pages",
        status: "active",
        parent: "root",
        readOnly: true,
      },
      {
        id: "tfcodeSession",
        droppable: true,
        name: "💻 TFcode sessions",
        status: "active",
        parent: "root",
        readOnly: true,
      },
      {
        id: "artifactsRoot",
        droppable: true,
        name: "🎨 Artifacts",
        status: "active",
        parent: "root",
        readOnly: true,
      },
      {
        id: "canvasRoot",
        droppable: true,
        name: "🖌 Canvas content",
        status: "active",
        parent: "root",
        readOnly: true,
      },
      ...adaptedFiles,
    ];

    dispatch("SET_MRC_TREEVIEW_DATA", treeData);
  };
  const handleEntityCreation = useCallback(
    (event) => {
      if (event === "success")
        AppToasty.show(t("mrcdocs.toasts.topicCreatedSuccess"), {
          type: "success",
        });
      else
        AppToasty.show(t("mrcdocs.toasts.topicCreationFailed"), {
          type: "danger",
        });
    },
    [AppToasty, t],
  );
  const handleEditorError = (event) => {
    console.error("error", event);
    if (event.message) {
      if (!displayedToasties()) {
        const id = AppToasty.show(t(event.message), {
          type: "danger",
        });
        displayedToasties(id);
      }
    }
  };
  const {
    handleFileSelect: handleFileSelectUnified,
    isFileUploadInProgress,
    filePath: filePathUnified,
    handleImports: handleImportsUnified,
    batchUploadStatus: batchUploadStatusUnified,
  } = useFileSelector({
    _importType: "imported-files",
    _importFile: "generic",
    contentType: "generic",
    additionalFilenameParams: ``,
    _appLogImportType: "documentImport",
    _appLogImportEvent: "readComprehensionContent file import",
    showSuccessOnUpload: false,
    flat: false,
    createDocument: true,
    uploadOnSelection: true,
    showPreview: true,
    allowMultiple: true,
    allowAllTypes: true,
    topics: topics,
    onShowPreview: (
      topics,
      folderName = null,
      fileCount = 0,
      pdfFiles = [],
      genericFiles = [],
    ) => {
      setShowModal(true);
      setPublishOnUpload(true);
      setUploadAdvEmb(false);
      setUploadExtractImages(false);
      setUploadFolderName(folderName || "");
      setUploadFileCount(fileCount);
      setUploadTargetFolderId(null);
      setCategorizedFiles({ pdfFiles, genericFiles });
      if (topics?.length == 1) {
        setSelectedTopicsForPdfUpload(generateAdaptedTopics([topics[0]]));
      } else {
        setSelectedTopicsForPdfUpload(null);
      }
      setTreeViewActionContext("UPLOAD_FILES");
    },
    onDocumentsCreated: (files) => {
      setDocuments([...documents, ...files]);
      if (inDropUploadRef.current) {
        dropCreatedRef.current = [...dropCreatedRef.current, ...files];
        return;
      }
      setShowModal(false);
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${files[0]?.id}`,
      );
    },
    onDocumentCreated: (file) => {
      setDocuments([...documents, file?.data[0]]);
      if (inDropUploadRef.current) {
        dropCreatedRef.current = [...dropCreatedRef.current, file?.data[0]];
        return;
      }
      setShowModal(false);
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${
          file?.data[0]?.id
        }`,
      );
    },
  });

  const {
    handleFileSelect: handleFolderSelectUnified,
    isFileUploadInProgress: isFolderUploadInProgress,
    filePath: folderPathUnified,
    handleImports: handleFolderImportsUnified,
    batchUploadStatus: batchUploadFolderStatusUnified,
  } = useFileSelector({
    _importType: "imported-files",
    _importFile: "generic",
    contentType: "generic",
    additionalFilenameParams: ``,
    _appLogImportType: "documentImport",
    _appLogImportEvent: "readComprehensionContent folder import",
    showSuccessOnUpload: false,
    flat: false,
    createDocument: true,
    uploadOnSelection: true,
    showPreview: true,
    allowMultiple: true,
    allowFolderUpload: true,
    allowAllTypes: true,
    topics: topics,
    onShowPreview: (
      topics,
      folderName = null,
      fileCount = 0,
      pdfFiles = [],
      genericFiles = [],
    ) => {
      setShowModal(true);
      setPublishOnUpload(true);
      setUploadAdvEmb(false);
      setUploadExtractImages(false);
      setUploadFolderName(folderName || "");
      setUploadFileCount(fileCount);
      setUploadTargetFolderId(null);
      setCategorizedFiles({ pdfFiles, genericFiles });
      if (topics?.length == 1) {
        setSelectedTopicsForPdfUpload(generateAdaptedTopics([topics[0]]));
      } else {
        setSelectedTopicsForPdfUpload(null);
      }
      setTreeViewActionContext("UPLOAD_FOLDER");
    },
    onDocumentsCreated: (files) => {
      setDocuments([...documents, ...files]);
      setShowModal(false);
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${files[0]?.id}`,
      );
    },
    onDocumentCreated: (file) => {
      setDocuments([...documents, file?.data[0]]);
      setShowModal(false);
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${
          file?.data[0]?.id
        }`,
      );
    },
  });

  const {
    handleFileSelect: handleArtifactSelect,
    isFileUploadInProgress: isArtifactUploadInProgress,
    filePath: artifactPath,
    handleImports: handleArtifactImportsUnified,
    batchUploadStatus: batchArtifactUploadStatus,
  } = useFileSelector({
    _importType: "imported-artifacts",
    _importFile: "artifact",
    contentType: "artifact",
    additionalFilenameParams: ``,
    _appLogImportType: "documentImport",
    _appLogImportEvent: "designArtifact file import",
    showSuccessOnUpload: false,
    flat: false,
    createDocument: true,
    uploadOnSelection: true,
    showPreview: true,
    allowMultiple: true,
    topics: topics,
    onShowPreview: (
      topics,
      folderName = null,
      fileCount = 0,
      pdfFiles = [],
      genericFiles = [],
      artifactFiles = [],
    ) => {
      setShowModal(true);
      setPublishOnUpload(true);
      setUploadAdvEmb(false);
      setUploadExtractImages(false);
      setUploadFolderName(folderName || "");
      setUploadFileCount(fileCount);
      setUploadTargetFolderId(null);
      setCategorizedFiles({ pdfFiles, genericFiles, artifactFiles });
      if (topics?.length == 1) {
        setSelectedTopicsForPdfUpload(generateAdaptedTopics([topics[0]]));
      } else {
        setSelectedTopicsForPdfUpload(null);
      }
      setTreeViewActionContext("UPLOAD_ARTIFACTS");
    },
    onDocumentsCreated: (files) => {
      setDocuments([...documents, ...files]);
      setShowModal(false);
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${files[0]?.id}`,
      );
    },
    onDocumentCreated: (file) => {
      setDocuments([...documents, file?.data[0]]);
      setShowModal(false);
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${
          file?.data[0]?.id
        }`,
      );
    },
  });

  const resetDropUploadState = () => {
    setDropFolderFiles([]);
    setDropFolderName("");
    setUploadFolderName("");
    setUploadFileCount(0);
    setDropTargetNode(null);
    dropTargetNodeRef.current = null;
    dropCreatedRef.current = [];
    setCategorizedFiles({ pdfFiles: [], genericFiles: [] });
  };

  const resolveTargetFolderId = (node) => {
    if (!node) return "mrcRoot";
    if (node?.data?.droppable) {
      const id = node?.data?.id || node?.id;
      return ["mrcRoot", "qaRoot", "domainRoot", "artifactsRoot", "tfcodeSession"].includes(id)
        ? id
        : id || "mrcRoot";
    }
    return node?.data?.parent || "mrcRoot";
  };

  const uploadFolderOptions = (state?.treeViewData || [])
    ?.filter((n) => n?.droppable)
    ?.map((n) => ({ id: n?.id, title: n?.name }))
    ?.sort((a, b) =>
      (a?.title || a?.id || "")?.localeCompare(b?.title || b?.id || ""),
    );
  const resolvedUploadTargetId =
    uploadTargetFolderId || resolveTargetFolderId(selectedNodeForTreeview);

  const handleDroppedFiles = async (dataTransfer, folderId) => {
    if (
      isFileUploadInProgress ||
      isFolderUploadInProgress ||
      isArtifactUploadInProgress
    )
      return;
    try {
      const result = await processDroppedItems(dataTransfer);
      if (!result?.supported) {
        AppToasty.show(t("mrcdocs.toasts.dropUnsupported"), {
          type: "danger",
        });
        return;
      }
      if (!result.allFiles?.length) {
        AppToasty.show(t("mrcdocs.toasts.noDroppableFiles"), {
          type: "warning",
        });
        return;
      }

      let targetNode = null;
      if (folderId) {
        const raw = state?.treeViewData?.find((u) => u?.id === folderId);
        if (raw) targetNode = { data: raw, id: raw?.id };
      }
      if (!targetNode) targetNode = selectedNodeForTreeview;
      dropTargetNodeRef.current = targetNode;
      setDropTargetNode(targetNode);
      setSelectedNodeForTreeView(targetNode);

      const firstFolder = result.folders?.[0];
      const looseCategorized = categorizeFiles(result.looseFiles);
      const folderFiles = firstFolder?.files || [];

      setCategorizedFiles({
        pdfFiles: looseCategorized.pdfFiles,
        genericFiles: looseCategorized.genericFiles,
        artifactFiles: looseCategorized.artifactFiles,
      });
      setDropFolderName(firstFolder?.name || "");
      setDropFolderFiles(folderFiles);
      setUploadFolderName(firstFolder?.name || "");
      setUploadFileCount(result.looseFiles.length + folderFiles.length);
      setPublishOnUpload(true);
      setUploadAdvEmb(false);
      setUploadExtractImages(false);
      if (topics?.length == 1) {
        setSelectedTopicsForPdfUpload(generateAdaptedTopics([topics[0]]));
      } else {
        setSelectedTopicsForPdfUpload(null);
      }
      setUploadTargetFolderId(null);
      setTreeViewActionContext("UPLOAD_DROP");
      setShowModal(true);
    } catch (error) {
      console.error("handleDroppedFiles error", error);
      AppToasty.show(t("mrcdocs.toasts.dropFolderError"), {
        type: "danger",
      });
    }
  };

  const handleDroppedFilesOnNode = (node, dataTransfer) => {
    return handleDroppedFiles(dataTransfer, node?.id);
  };

  const handleDroppedUpload = async () => {
    if (
      !selectedTopicsForPdfUpload ||
      selectedTopicsForPdfUpload?.length === 0
    ) {
      AppToasty.show(t("toasts.topics.selectAtLeastOne"), { type: "danger" });
      return;
    }
    const status = publishOnUpload ? "published" : "draft";
    const topicsArg = selectedTopicsForPdfUpload;
    const looseCat = categorizedFiles;
    const hasLoose =
      (looseCat.pdfFiles?.length || 0) +
        (looseCat.genericFiles?.length || 0) +
        (looseCat.artifactFiles?.length || 0) >
      0;
    const hasFolder = dropFolderFiles?.length > 0;
    if (!hasLoose && !hasFolder) {
      AppToasty.show(t("mrcdocs.toasts.noFilesToUpload"), {
        type: "danger",
      });
      return;
    }

    const targetNode = dropTargetNodeRef.current || selectedNodeForTreeview;
    const targetFolderId =
      uploadTargetFolderId || resolveTargetFolderId(targetNode);

    inDropUploadRef.current = true;
    dropCreatedRef.current = [];
    let subfolderId = null;
    try {
      if (hasFolder) {
        subfolderId = await createFolderForUpload(
          dropFolderName || t("mrcdocs.ui.uploadedFolder"),
        );
        if (!subfolderId) {
          inDropUploadRef.current = false;
          return;
        }
        const folderCategorized = categorizeFiles(dropFolderFiles);
        await handleImportsUnified(
          null,
          subfolderId,
          status,
          topicsArg,
          "generic",
          "imported-files",
          null,
          null,
          folderCategorized,
          uploadExtractImages,
          uploadAdvEmb,
        );
      }
      if (hasLoose) {
        await handleImportsUnified(
          null,
          targetFolderId,
          status,
          topicsArg,
          "generic",
          "imported-files",
          null,
          null,
          looseCat,
          uploadExtractImages,
          uploadAdvEmb,
        );
      }
    } catch (error) {
      console.error("handleDroppedUpload error", error);
      AppToasty.show(t("mrcdocs.toasts.fileUpdateFailed"), {
        type: "danger",
      });
    } finally {
      inDropUploadRef.current = false;
      const navId = subfolderId || dropCreatedRef.current?.[0]?.id || null;
      resetDropUploadState();
      setShowModal(false);
      if (navId) {
        history.push(
          `/workspaces/${getActiveWorkspace()?.id}/mrc_contents/${navId}`,
        );
      }
    }
  };

  const onMove = async (dropPayload) => {
    try {
      const dragIds = dropPayload?.dragIds || [];
      // Move every selected node (files and folders) like a Finder/Explorer
      // multi-drag. Folders update their `parent`; documents update `folderid`.
      const moveOne = async (dragId) => {
        const object = state?.treeViewData?.find((u) => u.id === dragId);
        if (!object) return;
        if (object?.droppable) {
          const index = folders.map((u) => u.id?.toString()).indexOf(object.id);
          let folder = { ...folders[index] };
          folder.parent = dropPayload.parentId;
          folders[index] = folder;
          await foldersHelper.updateFolder(object.id, [
            { property: "parent", value: dropPayload.parentId },
          ]);
        } else {
          // find the index of the document in the documents array
          const index = documents
            .map((u) => u.id?.toString())
            .indexOf(object.id);
          if (index < 0) return;
          documents[index].folderid = dropPayload.parentId;
          await updateDocumentTreeStructure({
            fields: {
              id: object.id,
              workspaceid: getActiveWorkspace()?.id,
              folderid: dropPayload.parentId,
            },
          });
        }
      };
      for (const dragId of dragIds) {
        await moveOne(dragId);
      }
      // Commit batched local state once after all moves.
      setFolders([...folders]);
      setDocuments([...documents]);
    } catch (error) {
      AppToasty.show(t("documents.errorMoving"), {
        type: "danger",
      });
    }
  };
  const onDrop = async ({ newTreeData, dragSourceId, dropTargetId, rest }) => {
    if (rest.dragSource?.data?.droppable) {
      await foldersHelper.updateFolder(dragSourceId, [
        { property: "parent", value: dropTargetId },
      ]);
    } else {
      await updateDocumentTreeStructure({
        fields: {
          id: dragSourceId,
          workspaceid: getActiveWorkspace()?.id,
          folderid: dropTargetId,
        },
      });
    }
    dispatch("SET_MRC_TREEVIEW_DATA", [...newTreeData]);
  };

  function treeify(list, idAttr, parentAttr, childrenAttr, targetNodeId) {
    if (!idAttr) idAttr = "id";
    if (!parentAttr) parentAttr = "parent";
    if (!childrenAttr) childrenAttr = "children";
    const _targetList = list?.filter((u) => u.id === targetNodeId);
    var treeList = [];
    var lookup = {};

    _targetList.forEach(function (obj) {
      lookup[obj[idAttr]] = obj;
      obj[childrenAttr] = [];
    });
    _targetList.forEach(function (obj) {
      if (obj[parentAttr] != null) {
        if (lookup[obj[parentAttr]] !== undefined) {
          lookup[obj[parentAttr]][childrenAttr].push(obj);
        } else {
          treeList.push(obj);
        }
      } else {
        treeList.push(obj);
      }
    });
    return treeList;
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <MrcDocumentsView
        setLastCancellation={setLastCancellation}
        isExtractImagesAllowed={isExtractImagesAllowed}
        embeddingsByDocumentInProgress={embeddingsByDocumentInProgress}
        extractImages={extractImages}
        setExtractImages={setExtractImages}
        advEmb={advEmb}
        setAdvEmb={setAdvEmb}
        embeddingStatus={embeddingStatus}
        embeddingsByDocumentResult={embeddingsByDocumentResult}
        folders={folders}
        deleteNode={removeNode}
        isMultiDeleteMode={isMultiDeleteMode}
        setIsMultiDeleteMode={setIsMultiDeleteMode}
        toggleMultiDeleteMode={toggleMultiDeleteMode}
        multiSelectedIds={multiSelectedIds}
        toggleNodeSelection={toggleNodeSelection}
        deleteMultipleNodes={deleteMultipleNodes}
        bulkMoveNodes={bulkMoveNodes}
        showBulkMoveModal={showBulkMoveModal}
        setShowBulkMoveModal={setShowBulkMoveModal}
        bulkMoveTargetFolderId={bulkMoveTargetFolderId}
        setBulkMoveTargetFolderId={setBulkMoveTargetFolderId}
        bulkMoveTopics={bulkMoveTopics}
        setBulkMoveTopics={setBulkMoveTopics}
        bulkMovePublish={bulkMovePublish}
        setBulkMovePublish={setBulkMovePublish}
        isBulkMoveInProgress={isBulkMoveInProgress}
        wordsCount={wordsCount}
        charactersCount={charactersCount}
        isInactive={isInactive}
        creationofNewDocumentInProgress={creationofNewDocumentInProgress}
        isReadyForRTC={isReadyForRTC}
        isDocumentValid={isDocumentValid && selectedNode}
        isLoading={isLoading}
        selectedNode={selectedNode}
        selectedNodeToDelete={selectedNodeToDelete}
        _coreTreeViewData={state.treeViewData}
        documentId={documentId}
        createDocument={createMRCDocument}
        SaveFolder={SaveFolder}
        handleEditorError={handleEditorError}
        value={value}
        onMove={onMove}
        handleOnRecorderClick={handleOnRecorderClick}
        setEditor={setEditor}
        documentStatus={documentStatus}
        onDocumentStatusChange={handleDocumentStatusChange}
        setValue={setValue}
        onSocketConnectionChange={handleSocketConnectionChange}
        handleClickOnNode={handleClickOnNode}
        setSelectedNodeForTreeView={setSelectedNodeForTreeView}
        setDialogActionOnTree={setDialogActionOnTree}
        showModal={showModal}
        setShowModal={setShowModal}
        treeViewActionContext={treeViewActionContext}
        documentFetchInProgress={documentFetchInProgress}
        fetchMRCDataInvoked={fetchMRCDataInvoked}
        storeInError={updateAPIError}
        storeInProgress={updateAPIInProgress || createAPIInProgress}
        isSaved={isSaved}
        selectedNodeForTreeview={selectedNodeForTreeview}
        selectedTopics={selectedTopics}
        setSelectedTopics={setSelectedTopics}
        downloadDocument={downloadDocument}
        updateDocumentFile={updateDocumentFile}
        onUpdateFileClick={onUpdateFileClick}
        updateFileInputRef={updateFileInputRef}
        isUpdateFileUploadInProgress={isUpdateFileUploadInProgress}
        onDroppedFiles={handleDroppedFiles}
        onDropFilesOnNode={handleDroppedFilesOnNode}
        handleDroppedUpload={handleDroppedUpload}
        dropFolderFiles={dropFolderFiles}
        dropFolderName={dropFolderName}
        dropTargetNode={dropTargetNode}
        onSelectedTopicsChange={onSelectedTopicsChange}
        topics={generateAdaptedTopics(topics)}
        handleEntityCreation={handleEntityCreation}
        handleFileSelect={handleFileSelectUnified}
        handleFolderSelect={handleFolderSelectUnified}
        handleArtifactSelect={handleArtifactSelect}
        handleArtifactImports={() => {
          handleArtifactImportsUnified(
            "designArtifact",
            resolvedUploadTargetId || "artifactsRoot",
            publishOnUpload ? "published" : "draft",
            selectedTopicsForPdfUpload,
            "artifact",
            "imported-artifacts",
            null,
            categorizedFiles,
          );
        }}
        onDrop={onDrop}
        isTreeDataLoading={
          documentFetchInProgress ||
          !fetchMRCDataInvoked ||
          !state?.foldersLoaded ||
          deletionInProgress ||
          isFileUploadInProgress ||
          isFolderUploadInProgress ||
          isArtifactUploadInProgress
        }
        editDocumentTitle={editDocumentTitle}
        agcEnabled={agcEnabled}
        fakeCheckInProgress={fakeCheckAPIInProgress}
        fakeCheckData={fakeCheckAPIResult}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isTopicUpdateModeEnabled={isTopicUpdateModeEnabled}
        setIsTopicUpdateModeEnabled={setIsTopicUpdateModeEnabled}
        filePath={filePathUnified}
        folderPath={folderPathUnified}
        artifactPath={artifactPath}
        onPDFDocumentLoad={onPDFDocumentLoad}
        isPDF={
          selectedNode?.data?.external_path != null &&
          selectedNode?.data?.parent != "domainRoot" &&
          selectedNode?.data?.parent != "artifactsRoot"
        }
        isPDFLoaded={isPDFLoaded}
        isPDFUploadInProgress={
          isFileUploadInProgress ||
          isFolderUploadInProgress ||
          isArtifactUploadInProgress
        }
        embeddingProgress={embeddingProgress}
        fileUploadProgress={0}
        setTreeViewActionContext={setTreeViewActionContext}
        setPublishOnUpload={setPublishOnUpload}
        publishOnUpload={publishOnUpload}
        uploadAdvEmb={uploadAdvEmb}
        setUploadAdvEmb={setUploadAdvEmb}
        uploadExtractImages={uploadExtractImages}
        setUploadExtractImages={setUploadExtractImages}
        handleImports={() => {
          handleImportsUnified(
            null,
            resolvedUploadTargetId,
            publishOnUpload ? "published" : "draft",
            selectedTopicsForPdfUpload,
            "generic",
            "imported-files",
            null,
            null,
            null,
            uploadExtractImages,
            uploadAdvEmb,
          );
        }}
        handleFolderImports={async (folderName) => {
          const finalFolderName =
            folderName || uploadFolderName || t("mrcdocs.ui.uploadedFolder");

          if (
            !categorizedFiles?.pdfFiles?.length &&
            !categorizedFiles?.genericFiles?.length
          ) {
            AppToasty.show(t("mrcdocs.toasts.noFilesToUpload"), {
              type: "danger",
            });
            return;
          }

          const newFolderId = await createFolderForUpload(
            finalFolderName,
            resolvedUploadTargetId,
          );
          if (!newFolderId) return;

          await handleFolderImportsUnified(
            null,
            newFolderId,
            publishOnUpload ? "published" : "draft",
            selectedTopicsForPdfUpload,
            "generic",
            "imported-files",
            null,
            null,
            null,
            uploadExtractImages,
            uploadAdvEmb,
          );
        }}
        batchUploadStatus={{
          ...batchUploadStatusUnified,
          ...batchUploadFolderStatusUnified,
          ...batchArtifactUploadStatus,
        }}
        documentImgRetrievalInstructions={documentImgRetrievalInstructions}
        setDocumentImgRetrievalInstructions={
          setDocumentImgRetrievalInstructions
        }
        selectedTopicsForPdfUpload={selectedTopicsForPdfUpload}
        setSelectedTopicsForPdfUpload={setSelectedTopicsForPdfUpload}
        onSelectedTopicsChangeForPdfUpload={onSelectedTopicsChangeForPdfUpload}
        searchText={searchText}
        setSearchText={setSearchText}
        promiseOptions={loadSuggestions}
        handleDocumentSelection={handleDocumentSelection}
        onTopicCreated={onTopicCreated}
        fetchEmbeddingsByDocument={fetchEmbeddingsByDocument}
        setIsSaveRequested={setIsSaveRequested}
        handleImagesExtraction={handleImagesExtraction}
        uploadFolderName={uploadFolderName}
        uploadFileCount={uploadFileCount}
        categorizedFiles={categorizedFiles}
        uploadFolderOptions={uploadFolderOptions}
        uploadTargetFolderId={resolvedUploadTargetId}
        onUploadTargetFolderChange={setUploadTargetFolderId}
      />
    </Suspense>
  );
};

export default MrcDocumentsContainer;
